import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './index.css'
import App from './App.jsx'
import { CartProvider } from './context/CartProvider.jsx'

/* ---------------------------------------------------------------------------
   BOOT HANDOFF — show the app once, never the prerendered snapshot.

   scripts/prerender.js writes the finished page into #root so crawlers get real
   HTML on the first response. React 18's createRoot does not adopt that markup:
   given a container with children it CLEARS it and renders from scratch. So the
   prerendered page is always thrown away — the only question is whether the
   visitor watched it happen.

   They did. And it was worse than a blank frame, because the snapshot does not
   look like the app:
     - prerender.js strips `opacity` from anything below 1 to undo GSAP's
       reveal states, which also flattens intentionally translucent chrome —
       the hero grid lines came back solid white.
     - it is captured once, at 1440x900, in one tab state, so the nav and the
       responsive blocks can differ from what this visitor renders.
   Keeping it on screen until React caught up (the previous attempt here) just
   traded a blank gap for two visibly different pages.

   So: #root is hidden before its first paint by the inline script in
   index.html, and revealed only once React has rendered the real page. The cost
   is a few hundred milliseconds of background colour. The gain is that a load
   is one page, once.

   hydrateRoot would let us keep the painted markup AND skip the re-render, but
   it is not available to us: initial state here comes from localStorage (theme,
   lang, tab), navigator.userAgent and the viewport, none of which the 1440x900
   prerender knew. Every mismatch makes React discard the markup anyway — same
   flash, plus console noise. Making that safe means moving every client-only
   read out of initial render across LandingPage and 19 solution pages.
--------------------------------------------------------------------------- */

const container = document.getElementById('root')
const docEl = document.documentElement

// Every route opens on the logo splash — the solution pages included, since
// they render SolutionPageTemplate which now boots the same way the main site
// does. So the snapshot is never shown to anyone: it exists for crawlers, and
// leaving it in place would only mean a settled hero flashing up before the
// splash covers it and the entrance animation plays over the top.
container.replaceChildren()

let revealed = false
const reveal = () => {
  if (revealed) return
  revealed = true
  docEl.classList.remove('cre8-booting')
  // React's tree was laid out inside a hidden (or display:none) container, so
  // every scroll position GSAP measured is stale. Re-measuring after it has
  // real dimensions keeps the pinned sections and scroll reveals from sticking.
  ScrollTrigger.refresh()
  window.dispatchEvent(new Event('resize'))
}

createRoot(container).render(
  <StrictMode>
    <CartProvider>
      <App />
    </CartProvider>
  </StrictMode>,
)

/* The splash IS the intended first frame everywhere now, so anything React has
   rendered is what we want on screen — no need to wait for a word count. */
const startedAt = performance.now()
const poll = () => {
  if (revealed) return
  const ready = container.childNodes.length > 0
  // The timeout is a guarantee, not an expectation: a route that never reaches
  // the bar (render error caught by RouteErrorBoundary, chunk 404) must still
  // become visible rather than sit behind a hidden #root.
  if (ready || performance.now() - startedAt > 8000) {
    reveal()
    return
  }
  requestAnimationFrame(poll)
}
requestAnimationFrame(poll)
